import { cn } from "@/lib/utils/cn";
import { useLayoutEffect, useRef, useState } from "react";

interface InlineExpandableTextProps {
  text: string;
  maxLines?: number;
  className?: string;
}

/**
 * Clamps long text to a fixed number of lines and places a clickable "more"
 * control on the last line (via float), so expansion doesn't cost an extra row.
 */
export const InlineExpandableText = ({
  text,
  maxLines = 3,
  className,
}: InlineExpandableTextProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [lineHeightPx, setLineHeightPx] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) {
      return;
    }

    const update = () => {
      measure.style.width = `${container.clientWidth}px`;

      const style = window.getComputedStyle(measure);
      let lineHeight = parseFloat(style.lineHeight);
      if (Number.isNaN(lineHeight)) {
        lineHeight = parseFloat(style.fontSize) * 1.5;
      }

      setLineHeightPx(lineHeight);
      setIsOverflowing(measure.scrollHeight > lineHeight * maxLines + 1);
    };

    update();

    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, [text, maxLines, className]);

  const isCollapsed = !isExpanded && isOverflowing && lineHeightPx > 0;
  const toggleClassName =
    "text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-semibold underline underline-offset-2";

  return (
    <div className="relative w-full min-w-0">
      <div
        ref={measureRef}
        aria-hidden
        className={cn(
          "pointer-events-none invisible absolute top-0 left-0 text-sm break-words",
          className,
        )}
      >
        {text}
      </div>

      <div
        ref={containerRef}
        className={cn("text-sm break-words", className)}
        style={
          isCollapsed
            ? { height: lineHeightPx * maxLines, overflow: "hidden" }
            : undefined
        }
      >
        {isCollapsed ? (
          <>
            <span
              aria-hidden
              className="float-right w-0"
              style={{ height: lineHeightPx * (maxLines - 1) }}
            />
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                setIsExpanded(true);
              }}
              className={cn("float-right clear-right ml-1", toggleClassName)}
              aria-label="Show more"
              aria-expanded={false}
            >
              more
            </button>
          </>
        ) : null}
        {text}
        {isExpanded && isOverflowing ? (
          <>
            {" "}
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                setIsExpanded(false);
              }}
              className={cn("inline", toggleClassName)}
              aria-label="Show less"
              aria-expanded
            >
              less
            </button>
          </>
        ) : null}
      </div>
    </div>
  );
};
